import React, { useMemo, useState } from 'react';
import { Typography, Alert, Button, Space, Descriptions, Card } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import TopbarStructureViewer from '../../structure-analysis/TopbarStructureViewer';

const { Text } = Typography;

interface ProtenixResultProps {
  taskResult?: any;
  data?: any;
  title?: string;
  showTitle?: boolean;
}

const ProtenixResult: React.FC<ProtenixResultProps> = ({ 
  taskResult, 
  data, 
  title = "Protenix 蛋白质结构预测结果", 
  showTitle = true 
}) => {
  // 提取Protenix结构结果
  const getStructureResult = () => {
    // 优先级1: data参数（从props直接传入）
    if (data) {
      // 如果data已经是处理好的结果对象
      if (data.pdb_content || data.structure_content) {
        return data;
      }
      // 如果data是包装格式
      if (data.results && Array.isArray(data.results) && data.results.length > 0) {
        const firstResult = data.results[0];
        if (firstResult.result) {
          return firstResult.result;
        }
        return firstResult;
      }
    }
    
    // 优先级2: taskResult.result格式（Profile页面格式）
    if (taskResult?.result) {
      // 格式1: taskResult.result.results[0].result（任务管理器包装格式）
      if (taskResult.result.results && Array.isArray(taskResult.result.results) && taskResult.result.results.length > 0) {
        const firstResult = taskResult.result.results[0];
        if (firstResult.result) {
          return firstResult.result;
        }
        return firstResult;
      }
      
      // 格式2: taskResult.result 本身就是结果
      if (taskResult.result.pdb_content || taskResult.result.structure_content) {
        return taskResult.result;
      }
    }
    
    // 优先级3: taskResult 本身
    if (taskResult?.pdb_content || taskResult?.structure_content) {
      return taskResult;
    }
    
    return null;
  };

  const result = getStructureResult();
  
  // 状态：当前选中的样本
  const [currentSample, setCurrentSample] = useState<any>(null);

  if (!result) {
    return (
      <Alert
        type="info"
        message="无Protenix结构预测结果"
        description="任务可能还在运行中或者结果格式不正确"
        showIcon
      />
    );
  }

  // 获取结构内容（兼容多种字段名）
  const structureContent = currentSample?.structure_content || result.pdb_content || result.structure_content;
  const structureFormat: 'pdb' | 'cif' | 'sdf' | undefined = useMemo(() => {
    if (!structureContent) return undefined as any;
    const s = structureContent.trim();
    if (s.startsWith('data_') || s.includes('_atom_site.')) return 'cif';
    return 'pdb';
  }, [structureContent]);
  
  // 获取所有样本（如果有）
  const samples = result.samples || [];
  const hasSamples = samples.length > 0;

  // 获取当前样本的置信度分数
  const confidenceScores = currentSample?.confidence_scores || result.confidence_scores;

  // 下载结构文件
  const downloadStructure = (content: string, sampleId?: string) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const fileName = sampleId 
      ? `${result.name || 'protein'}_sample_${sampleId}.cif`
      : `${result.name || 'protein'}.cif`;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  // 下载所有样本（如果有）
  const downloadAllSamples = () => {
    if (!hasSamples) {
      downloadStructure(structureContent);
      return;
    }

    samples.forEach((sample: any, index: number) => {
      setTimeout(() => {
        downloadStructure(sample.structure_content, sample.sample_id);
      }, index * 100);
    });
  };

  if (!structureContent) {
    return (
      <Alert
        type="warning"
        message="结构数据缺失"
        description="预测结果中没有找到结构内容"
        showIcon
      />
    );
  }

  return (
    <div>
      {showTitle && (
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ fontSize: 16 }}>{title}</Text>
        </div>
      )}

      {/* 基本信息 */}
      <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="结构名称">{result.name || '未命名'}</Descriptions.Item>
        <Descriptions.Item label="预测模式">
          {result.prediction_mode === 'monomer' ? '单体模式' : '复合物模式'}
        </Descriptions.Item>
        {result.sequence_count && (
          <Descriptions.Item label="序列数量">{result.sequence_count}</Descriptions.Item>
        )}
        {hasSamples && (
          <Descriptions.Item label="预测样本数">{samples.length}</Descriptions.Item>
        )}
      </Descriptions>

      {/* 多样本选择 */}
      {hasSamples && (
        <div style={{ marginBottom: 16, padding: '12px', background: '#fafafa', borderRadius: '4px' }}>
          <span style={{ marginRight: 8, fontWeight: 'bold' }}>选择样本：</span>
          {samples.map((sample: any, index: number) => {
            const isActive = currentSample?.sample_id === sample.sample_id;
            
            return (
              <Button
                key={index}
                size="small"
                type={isActive ? 'primary' : 'default'}
                onClick={() => setCurrentSample(sample)}
                style={{ marginRight: 8 }}
              >
                Sample {sample.sample_id}
              </Button>
            );
          })}
        </div>
      )}

      {/* 置信度分数显示 */}
      {confidenceScores && (
        <Card size="small" title="置信度分数" style={{ marginBottom: 16 }}>
          <Space direction="horizontal" size="large" wrap>
            {confidenceScores.plddt !== undefined && (
              <span>
                <strong>pLDDT:</strong>{' '}
                <span style={{ color: confidenceScores.plddt > 70 ? '#52c41a' : '#ff4d4f' }}>
                  {confidenceScores.plddt.toFixed(2)}
                </span>
              </span>
            )}
            {confidenceScores.gpde !== undefined && (
              <span><strong>GPDE:</strong> {confidenceScores.gpde.toFixed(3)}</span>
            )}
            {confidenceScores.ptm !== undefined && (
              <span>
                <strong>pTM:</strong>{' '}
                <span style={{ color: confidenceScores.ptm > 0.7 ? '#52c41a' : '#ff4d4f' }}>
                  {confidenceScores.ptm.toFixed(3)}
                </span>
              </span>
            )}
            {confidenceScores.iptm !== undefined && confidenceScores.iptm > 0 && (
              <span>
                <strong>ipTM:</strong>{' '}
                <span style={{ color: confidenceScores.iptm > 0.7 ? '#52c41a' : '#ff4d4f' }}>
                  {confidenceScores.iptm.toFixed(3)}
                </span>
              </span>
            )}
            {confidenceScores.ranking_score !== undefined && (
              <span><strong>Ranking Score:</strong> {confidenceScores.ranking_score.toFixed(4)}</span>
            )}
          </Space>
        </Card>
      )}

      {/* 操作按钮 */}
      <Space style={{ marginBottom: 16 }}>
        <Button 
          icon={<DownloadOutlined />}
          onClick={() => downloadStructure(structureContent, currentSample?.sample_id)}
        >
          下载当前样本
        </Button>
        {hasSamples && (
          <Button 
            icon={<DownloadOutlined />}
            onClick={downloadAllSamples}
          >
            下载所有样本 ({samples.length})
          </Button>
        )}
      </Space>

      {/* 顶部序列 + 结构可视化（双向高亮） */}
      <div style={{ height: 600, border: '1px solid #d9d9d9', borderRadius: '4px', overflow: 'hidden' }}>
        <TopbarStructureViewer
          data={structureContent}
          format={structureFormat}
          fileName={`${result.name || 'model'}${currentSample ? `_sample_${currentSample.sample_id}` : ''}`}
          height={600}
          showSequence
        />
      </div>
    </div>
  );
};

export default ProtenixResult;

